import { isValidElement, useEffect, useMemo } from 'react'

import { useNavigate } from 'react-router-dom'

import clsx from 'clsx'
import { observer } from 'mobx-react-lite'

import { RoutingPaths } from 'app/routes/routes.ts'

import { getDataForSearchTour } from 'pages/tours/search-result-page/libs/getDataForSearchTour.tsx'
import { useHeaderMeta } from 'pages/tours/search-result-page/model/hooks/useHeaderMeta.ts'

import searchTourStore from 'entities/tour/models/search-tour/search-tour'
import tourListStore from 'entities/tour/models/tour-list/tour-list.ts'

import { SavedFormKeys, saveForm } from 'shared/hooks'
import { AppAdviceText } from 'shared/ui/app-advice-text'
import { useApiErrorDialog } from 'shared/ui/dialog-bottom-sheet/lib/useApiErrorDialog'
import { useDialogBottomSheet } from 'shared/ui/dialog-bottom-sheet/lib/useDialogBottomSheet'
import dialogBottomSheetStore from 'shared/ui/dialog-bottom-sheet/model/dialog-bottom-sheet-store'
import { Loader } from 'shared/ui/loader'

import { AlmatySearchPopup } from './ui/almaty-search-popup'

import { EmptyResults, SearchEditGuide } from './components'
import { SearchResultList } from './components/search-result-list'
import { ToursHeader } from './components/tours-header'
import s from './styles.module.scss'

const EMPTY_PAGE = 0

const SearchResultPage = observer(() => {
  const navigate = useNavigate()
  const { openDialog, closeDialog } = useDialogBottomSheet()
  const { showApiErrorDialog } = useApiErrorDialog()
  const almatyDeparture = tourListStore.almatyDeparture
  const hasShownAlmatyOffer = tourListStore.hasShownAlmatyOffer
  const searchError = tourListStore.error
  const isSearchEditing = searchTourStore.getIsSearchEditing()
  const isDialogOpen = dialogBottomSheetStore.isShow

  useEffect(() => {
    if (!almatyDeparture || hasShownAlmatyOffer || isSearchEditing) return
    tourListStore.markAlmatyOfferShown()
    const popup = (
      <AlmatySearchPopup
        key="almaty-search-offer"
        onClose={closeDialog}
        onConfirm={() => {
          closeDialog()
          void tourListStore.retryFromAlmaty()
          const updatedForm = searchTourStore.getDataForSearchTour()
          if (updatedForm) saveForm(updatedForm, SavedFormKeys.tours.key)
        }}
      />
    )
    openDialog({
      isDialog: false,
      child: popup,
      padding: [20],
      borderRadius: 20,
    })
  }, [
    almatyDeparture,
    hasShownAlmatyOffer,
    isSearchEditing,
    openDialog,
    closeDialog,
  ])

  useEffect(() => {
    if (searchError) showApiErrorDialog(searchError)
  }, [searchError, showApiErrorDialog])

  useEffect(
    () => () => {
      const child = dialogBottomSheetStore.child
      if (isValidElement(child) && child.key === 'almaty-search-offer')
        closeDialog()
    },
    [closeDialog],
  )

  const { title, subtitle } = useHeaderMeta()

  const dataForSearchTour = searchTourStore.getDataForSearchTour()
  const tours = tourListStore.getTourList()
  const orderBy = searchTourStore.getOrderBy()
  const isLoading = tourListStore.isLoading

  const requestData = useMemo(
    () => ({
      ...getDataForSearchTour(dataForSearchTour),
      order_by: orderBy,
    }),
    [dataForSearchTour, orderBy],
  )

  useEffect(() => {
    if (searchTourStore.getIsSearchEditing()) {
      searchTourStore.cancelSearchEdit()

      return
    }

    if (!dataForSearchTour || tourListStore.lastRequestData || tours?.length)
      return

    saveForm(dataForSearchTour, SavedFormKeys.tours.key)
    void tourListStore.requestToursList(requestData)
  }, [])

  useEffect(() => {
    const currentDataForSearch = searchTourStore.getDataForSearchTour()
    if (!currentDataForSearch?.country) {
      navigate(RoutingPaths.tours.path())
    }
  }, [navigate])

  const handleGoBack = () => {
    navigate(RoutingPaths.tours.path())
  }

  const handleEditSearch = () => {
    searchTourStore.beginSearchEdit()
    navigate(RoutingPaths.tours.searchForm.path())
  }

  return (
    <div className={s.container}>
      <ToursHeader
        onGoBack={handleGoBack}
        title={
          dataForSearchTour?.departure?.name
            ? `${dataForSearchTour.departure.name} → ${title}`
            : title
        }
        subtitle={subtitle}
        onEditSearch={handleEditSearch}
      />
      {!isLoading && !isDialogOpen && !searchError && <SearchEditGuide />}

      {!isLoading &&
        tourListStore.hasSearchSucceeded &&
        tourListStore.listOfTours?.length === EMPTY_PAGE && <EmptyResults />}

      <div className={s.searchResult}>
        {isLoading && tourListStore.listOfTours?.length === EMPTY_PAGE ? (
          <div className={clsx('container', s.loader)}>
            <Loader marginTop={200} />

            <AppAdviceText />
          </div>
        ) : (
          <SearchResultList data={tours} />
        )}
      </div>
    </div>
  )
})

export default SearchResultPage
